import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

/* ============================================================
   PORTAL PÚBLICO DO SUBCONTRATADO (SGI — ISO 45001, 8.1.4)
   O subcontratado acede por link único (token, sem login) e
   carrega os documentos de SST. Fora do Layout (como o onboarding).
   ============================================================ */

const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };

const SubcontratadoPortal = () => {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [erro, setErro] = useState('');
    const [loading, setLoading] = useState(true);
    // upload
    const [tipo, setTipo] = useState('');
    const [ficheiro, setFicheiro] = useState(null);
    const [dataValidade, setDataValidade] = useState('');
    const [dataEmissao, setDataEmissao] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [msg, setMsg] = useState('');

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/subcontratados/public/${token}`);
            setData(r.data);
        } catch (e) {
            setErro(e.response?.data?.error || 'Não foi possível abrir o portal.');
        } finally { setLoading(false); }
    }, [token]);
    useEffect(() => { carregar(); }, [carregar]);

    const tipoInfo = data?.tipos_documento?.find(t => t.tipo === tipo);

    const enviar = async (e) => {
        e.preventDefault();
        if (!tipo) { setMsg('Escolha o tipo de documento.'); return; }
        if (!ficheiro) { setMsg('Selecione o ficheiro.'); return; }
        setEnviando(true); setMsg('');
        try {
            const fd = new FormData();
            fd.append('tipo', tipo);
            fd.append('ficheiro', ficheiro);
            if (dataValidade) fd.append('data_validade', dataValidade);
            if (dataEmissao) fd.append('data_emissao', dataEmissao);
            await api.post(`/subcontratados/public/${token}/documento`, fd);
            setTipo(''); setFicheiro(null); setDataValidade(''); setDataEmissao('');
            if (document.getElementById('sc-file')) document.getElementById('sc-file').value = '';
            setMsg('✓ Documento enviado. Obrigado!');
            carregar();
        } catch (err) {
            setMsg(err.response?.data?.error || 'Erro ao enviar o documento.');
        } finally { setEnviando(false); }
    };

    const wrap = { minHeight: '100vh', background: '#f1f5f9', padding: '24px 16px', fontFamily: 'system-ui, sans-serif' };
    const card = { maxWidth: 620, margin: '0 auto', background: '#fff', borderRadius: 14, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: 26 };

    if (loading) return <div style={wrap}><div style={card}>A carregar…</div></div>;
    if (erro) return <div style={wrap}><div style={{ ...card, textAlign: 'center' }}><h2 style={{ color: '#991b1b' }}>⚠️ {erro}</h2></div></div>;

    const labelDoc = (t) => data.tipos_documento.find(x => x.tipo === t)?.label || t;

    return (
        <div style={wrap}>
            <div style={card}>
                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                    <h1 style={{ margin: 0, fontSize: '1.4rem', color: '#1e293b' }}>Documentos de Segurança</h1>
                    <p style={{ color: '#64748b', margin: '6px 0 0' }}>SPROD · <strong>{data.nome}</strong>{data.tipo_servico ? ` — ${data.tipo_servico}` : ''}</p>
                </div>

                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 16px', fontSize: '0.88rem', color: '#1e40af', marginBottom: 20 }}>
                    Para trabalhar em obras da SPROD, carregue aqui os documentos de segurança e saúde no trabalho.
                    Indique a <strong>data de validade</strong> sempre que aplicável (seguros, aptidão médica).
                </div>

                {/* Documentos já carregados */}
                {data.documentos.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>Já enviados</div>
                        {data.documentos.map(d => (
                            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 6 }}>
                                <span style={{ fontSize: '1.1rem' }}>✅</span>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, color: '#334155', fontSize: '0.9rem' }}>{labelDoc(d.tipo)}</div>
                                    <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                                        {d.nome_original}{d.data_validade ? ` · válido até ${fmtData(d.data_validade)}` : ''}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Formulário de upload */}
                <form onSubmit={enviar}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>Enviar documento</div>
                    <label style={lbl}>Tipo de documento</label>
                    <select value={tipo} onChange={e => setTipo(e.target.value)} style={{ ...inp, width: '100%', marginBottom: 12 }}>
                        <option value="">— escolher —</option>
                        {data.tipos_documento.map(t => <option key={t.tipo} value={t.tipo}>{t.label}</option>)}
                    </select>

                    {tipoInfo?.validade && (
                        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: 130 }}>
                                <label style={lbl}>Data de emissão</label>
                                <input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} style={{ ...inp, width: '100%' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 130 }}>
                                <label style={lbl}>Válido até</label>
                                <input type="date" value={dataValidade} onChange={e => setDataValidade(e.target.value)} style={{ ...inp, width: '100%' }} />
                            </div>
                        </div>
                    )}

                    <label style={lbl}>Ficheiro (PDF ou imagem)</label>
                    <input id="sc-file" type="file" onChange={e => setFicheiro(e.target.files?.[0] || null)} style={{ marginBottom: 14, display: 'block' }} />

                    <button type="submit" disabled={enviando} style={{
                        width: '100%', padding: '12px', border: 'none', borderRadius: 10, background: '#2563eb', color: '#fff',
                        fontSize: '1rem', fontWeight: 600, cursor: enviando ? 'default' : 'pointer', opacity: enviando ? 0.6 : 1,
                    }}>
                        {enviando ? 'A enviar…' : 'Enviar documento'}
                    </button>
                    {msg && <div style={{ marginTop: 12, textAlign: 'center', fontSize: '0.9rem', color: msg.startsWith('✓') ? '#166534' : '#991b1b' }}>{msg}</div>}
                </form>

                <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#94a3b8', marginTop: 20 }}>
                    Pode voltar a este link a qualquer momento para atualizar os documentos.
                </p>
            </div>
        </div>
    );
};

const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' };

export default SubcontratadoPortal;
